import React from 'react';
import PropTypes from 'prop-types';
import './Button.css';

/**
 * Button Component
 * 
 * A reusable button component that uses design tokens for consistent styling.
 * Supports multiple variants, sizes, states, and can include icons.
 * 
 * @example
 * // Primary button
 * <Button variant="primary" onClick={handleClick}>Click Me</Button>
 * 
 * @example
 * // Button with loading state
 * <Button variant="primary" isLoading>Submitting...</Button>
 * 
 * @example
 * // Button with icon
 * <Button variant="secondary" iconLeft="🔍">Search</Button>
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled = false,
  isLoading = false,
  fullWidth = false,
  iconLeft = null,
  iconRight = null,
  onClick,
  className = '',
  ...props
}) => {
  const baseClass = 'btn';
  const variantClass = `btn--${variant}`;
  const sizeClass = `btn--${size}`;
  const fullWidthClass = fullWidth ? 'btn--full-width' : '';
  const loadingClass = isLoading ? 'btn--loading' : '';
  const disabledClass = disabled || isLoading ? 'btn--disabled' : '';
  
  const classNames = [
    baseClass,
    variantClass,
    sizeClass,
    fullWidthClass,
    loadingClass,
    disabledClass,
    className
  ].filter(Boolean).join(' ');

  const handleClick = (e) => {
    if (!disabled && !isLoading && onClick) {
      onClick(e);
    }
  };

  return (
    <button
      type={type}
      className={classNames}
      onClick={handleClick}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      aria-disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <span className="btn__spinner" aria-hidden="true"></span>
      )}
      
      {!isLoading && iconLeft && (
        <span className="btn__icon btn__icon--left" aria-hidden="true">
          {iconLeft}
        </span>
      )}
      
      <span className="btn__content">
        {children}
      </span>
      
      {!isLoading && iconRight && (
        <span className="btn__icon btn__icon--right" aria-hidden="true">
          {iconRight}
        </span>
      )}
    </button>
  );
};

Button.propTypes = {
  /** Button content */
  children: PropTypes.node.isRequired,
  
  /** Button style variant */
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'ghost', 'danger']),
  
  /** Button size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  
  /** HTML button type */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  
  /** Disable the button */
  disabled: PropTypes.bool,
  
  /** Show loading spinner */
  isLoading: PropTypes.bool,
  
  /** Make button full width */
  fullWidth: PropTypes.bool,
  
  /** Icon to display on the left */
  iconLeft: PropTypes.node,
  
  /** Icon to display on the right */
  iconRight: PropTypes.node,
  
  /** Click handler */
  onClick: PropTypes.func,
  
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default Button;
